import type { IAgentTool } from '../IAgentTool';
import { normalizeField } from '../../../assistant/utils/conditionEvaluation';

const NAMED_COLORS: Record<string, string> = {
  blue: '#3b82f6',
  red: '#ef4444',
  green: '#22c55e',
  yellow: '#eab308',
  orange: '#f97316',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  gray: '#6b7280',
  grey: '#6b7280',
  black: '#111827',
  white: '#ffffff',
};

const cleanPhrase = (value: unknown): string => String(value ?? '').trim();

const cleanFieldInput = (value: unknown): string =>
  cleanPhrase(value)
    .replace(/^(?:the|a|an)\s+/i, '')
    .replace(/\s+(?:field|column)$/i, '')
    .trim();

const cleanTargetValue = (value: unknown): string =>
  cleanPhrase(value)
    .replace(/^(?:the|a|an)\s+/i, '')
    .replace(/\s+(?:department|dept|team)$/i, '')
    .replace(/[.,!?]+$/g, '')
    .trim();

export const normalizeAgentColor = (value: string): string | null => {
  const trimmed = value
    .trim()
    .replace(/^(?:to|in|with)\s+/i, '')
    .replace(/[.,!?]+$/g, '')
    .toLowerCase();
  if (!trimmed) return null;
  if (/^#[0-9a-f]{6}$/i.test(trimmed) || /^#[0-9a-f]{3}$/i.test(trimmed)) {
    return trimmed;
  }
  return NAMED_COLORS[trimmed] ?? null;
};

const resolveField = (params: Record<string, unknown>): string => {
  const rawField = cleanFieldInput(params.field ?? params.fieldId ?? params.matchField);
  if (rawField) {
    const normalizedField = normalizeField(rawField);
    if (normalizedField === 'gender' || normalizedField === 'genders') return 'sex';
    return normalizedField;
  }
  if (params.department != null) return 'department';
  return '';
};

const resolveValue = (params: Record<string, unknown>): string =>
  cleanTargetValue(
    params.value ??
      params.fieldValue ??
      params.matchValue ??
      params.targetValue ??
      params.department,
  );

const getTextColor = (hexColor: string): string => {
  const value = hexColor.replace('#', '');
  const normalized = value.length === 3
    ? value.split('').map((char) => `${char}${char}`).join('')
    : value;

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);

  return luminance > 186 ? '#111827' : '#ffffff';
};

export const colorMatchTool: IAgentTool = {
  name: 'color_match',
  description:
    'Color nodes matching a specific field value with a requested color (for example, color department = Marketing blue).',
  parameters: {
    type: 'object',
    properties: {
      field: { type: 'string', description: 'Field to match, for example department' },
      value: { type: 'string', description: 'Field value to match, for example Marketing' },
      color: { type: 'string', description: 'Named color or hex color, for example blue or #3b82f6' },
    },
    required: ['field', 'value', 'color'],
  },
  supportedDiagramTypes: [],
  tier: 'visual',
  category: 'display',

  async execute(params, context) {
    const field = resolveField(params);
    const value = resolveValue(params);
    const color = normalizeAgentColor(String(params.color ?? params.backgroundColor ?? params.fillColor ?? ''));

    if (!field || !value || !color) {
      return {
        success: false,
        summary: 'Missing or invalid field, value, or color for targeted coloring.',
      };
    }

    const ruleId = context.stores.viewRules.addViewRule('highlight', {
      name: `${field}: ${value}`,
    });

    if (!ruleId) {
      return { success: false, summary: 'Could not create a targeted color rule.' };
    }

    context.stores.viewRules.addConditionToRule(ruleId, {
      field,
      operator: 'equals',
      value,
    });
    context.stores.viewRules.updateRuleDisplay(ruleId, {
      scopeMode: 'custom',
      colors: {
        backgroundColor: color,
        borderColor: color,
        textColor: getTextColor(color),
      },
    });

    return {
      success: true,
      summary: `Colored ${field} "${value}" ${color}.`,
      createdResourceIds: [ruleId],
    };
  },
};
